import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {useState, useEffect} from 'react';
// @ts-ignore -- tz-lookup ships no types
import tzlookup from 'tz-lookup';
import {apartmentServiceCreate, apartmentServiceUpdate} from 'src/apis/autoGenerate/services';
import {Apartment, PropertyType} from 'src/apis/autoGenerate/types/apartment';
import {GeoLocation} from 'src/apis/autoGenerate/types/geoLocation';
import {IdRsp} from 'src/apis/autoGenerate/common';
import message from 'src/components/Message';
import Modal from 'src/components/Modal';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import PlacesAutocomplete, {PlaceType} from '../AddApartment/PlacesAutocomplete';
import {useGoogleMapContext} from '../AddApartment/context';
import {useNavigate} from 'react-router-dom';
import {TIMEZONE_OPTIONS, DEFAULT_TIMEZONE} from 'src/constants/timezones';

interface AddCommercialPropertyModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleConfirm: (newPropertyId?: string) => void;
  initData?: Apartment | null; // Add this line
}

function getInitFormData() {
  return {
    propertyName: '',
    description: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    timeZone: DEFAULT_TIMEZONE,
  };
}

function getInitFormErrors() {
  return {
    propertyName: false,
  };
}

export default function AddCommercialPropertyModal({
  open,
  setOpen,
  handleConfirm,
  initData, // Add this parameter
}: AddCommercialPropertyModalProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(selectAccount);

  // Initialize form data with existing data if editing
  const [formData, setFormData] = useState(
    initData
      ? {
          propertyName: initData.name || '',
          description: initData.description || '',
          street: initData.street || '',
          city: initData.city || '',
          state: initData.state || '',
          zipCode: initData.zipCode || '',
          timeZone: initData.timeZone || DEFAULT_TIMEZONE,
        }
      : getInitFormData()
  );
  const [formErrors, setFormErrors] = useState(getInitFormErrors());

  const googleMapContext = useGoogleMapContext();
  // Initialize position with existing location if editing
  const [position, setPosition] = useState<GeoLocation | null>(
    initData?.location ? initData.location : null
  );
  const [geocode, setGeocode] = useState<any>(googleMapContext?.geocode);
  const [places, setPlaces] = useState<any>(googleMapContext?.places);

  useEffect(() => {
    if (googleMapContext?.geocode && !geocode) {
      setGeocode(googleMapContext.geocode);
    }
    if (googleMapContext?.places && !places) {
      setPlaces(googleMapContext.places);
    }
  }, [googleMapContext, places, geocode]);

  const handleChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });

      // Clear error when user starts typing
      if (formErrors[field as keyof typeof formErrors]) {
        setFormErrors({
          ...formErrors,
          [field]: false,
        });
      }
    };

  const handleCreateCommercialProperty = () => {
    // Validate form
    const errors = {
      propertyName: !formData.propertyName.trim(),
    };

    setFormErrors(errors);

    if (errors.propertyName) {
      message.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    if (account?.userDetail?.organizations && account?.userDetail?.organizations[0].id) {
      const apartmentData: Apartment = {
        name: formData.propertyName,
        description: formData.description,
        location: position || undefined,
        orgId: account.userDetail?.organizations[0].id as string,
        propertyType: PropertyType.COMMERCIAL,
        // Add the parsed address components
        street: formData.street || '',
        city: formData.city || '',
        state: formData.state || '',
        zipCode: formData.zipCode || '',
        timeZone: formData.timeZone,
      };

      apartmentServiceCreate({apartment: apartmentData})
        .then(res => {
          const {id} = res.data as IdRsp;
          message.success('Commercial property created successfully');
          handleConfirm(id);
          // Navigate to commercial detail page
          navigate(`/property/commercialDetail/${id}`);
        })
        .catch(err => {
          message.error('Failed to create: ' + err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Add edit handler
  const handleEditCommercialProperty = () => {
    // Validation logic (same as create)
    const errors = {
      propertyName: !formData.propertyName.trim(),
    };

    setFormErrors(errors);

    if (errors.propertyName) {
      message.error('Please fill in all required fields');
      return;
    }

    if (initData) {
      setLoading(true);
      const apartmentData: Apartment = {
        ...initData,
        name: formData.propertyName,
        description: formData.description,
        location: position || undefined,
        street: formData.street || '',
        city: formData.city || '',
        state: formData.state || '',
        zipCode: formData.zipCode || '',
        propertyType: PropertyType.COMMERCIAL,
        timeZone: formData.timeZone,
      };

      apartmentServiceUpdate(initData.id as string, {apartment: apartmentData})
        .then(() => {
          message.success('Commercial property updated successfully');
          handleConfirm();
        })
        .catch(err => {
          message.error('Failed to update: ' + err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handlePlaceSelect = (place: PlaceType | null) => {
    if (place && geocode) {
      updateMap(place);

      // Extract the main address line for description
      const address = place.structured_formatting.main_text;

      // Initialize variables for address components
      let street = '';
      let city = '';
      let state = '';
      let zipCode = '';

      try {
        // Get the geocoded address components.
        // Use placeId -- reliable regardless of the place description's language.
        geocode.geocode({placeId: place.place_id}, (results: any, status: any) => {
          if (status === 'OK' && results && results[0] && results[0].address_components) {
            // Extract address components using Google's address_components
            for (const component of results[0].address_components) {
              const types = component.types;

              if (types.includes('street_number')) {
                street = component.long_name + ' ' + street;
              }
              if (types.includes('route')) {
                street = street + component.long_name;
              }
              if (types.includes('locality')) {
                city = component.long_name;
              }
              if (types.includes('administrative_area_level_1')) {
                state = component.short_name;
              }
              if (types.includes('postal_code')) {
                zipCode = component.long_name;
              }
            }

            // Resolve timezone + position from this geocode result.
            let resolvedTz: string | undefined;
            try {
              const geom = results[0].geometry?.location;
              if (geom) {
                const lat = typeof geom.lat === 'function' ? geom.lat() : geom.lat;
                const lng = typeof geom.lng === 'function' ? geom.lng() : geom.lng;
                if (lat != null && lng != null) {
                  const tz = tzlookup(Number(lat), Number(lng));
                  if (tz) resolvedTz = tz;
                  setPosition({latitude: Number(lat), longitude: Number(lng)});
                }
              }
            } catch {
              // tz-lookup throws for out-of-range coords -- ignore.
            }

            // Update the form data with extracted components.
            // Functional setState so we don't clobber parallel updateMap() updates.
            setFormData(prev => ({
              ...prev,
              description: place.description,
              street: street.trim(),
              city: city,
              state: state,
              zipCode: zipCode,
              ...(resolvedTz ? {timeZone: resolvedTz} : {}),
            }));
          }
        });
      } catch (error) {
        console.error('Error parsing address:', error);
        // Fallback: just set the description
        setFormData(prev => ({
          ...prev,
          description: place.description,
        }));
      }
    }
  };

  async function updateMap(place: PlaceType) {
    if (geocode && place) {
      try {
        await new Promise((resolve, reject) => {
          geocode.geocode({placeId: place.place_id}, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              const location = results[0].geometry.location;
              const lat = Number(location.lat());
              const lng = Number(location.lng());
              setPosition({latitude: lat, longitude: lng});
              // Auto-fill timezone from coordinates; user can still override.
              try {
                const tz = tzlookup(lat, lng);
                if (tz) {
                  setFormData(prev => ({...prev, timeZone: tz}));
                }
              } catch {
                // tz-lookup throws for out-of-range coords -- ignore.
              }
              resolve(results);
            } else {
              reject(new Error('Geocoding failed'));
            }
          });
        });
      } catch (error) {
        console.error('Error geocoding:', error);
      }
    }
  }

  return (
    <Modal
      loading={loading}
      open={open}
      title={initData ? 'Edit Commercial Property' : 'Add Commercial Property'}
      handleCancel={() => {
        setOpen(false);
        setFormData(getInitFormData());
        setFormErrors(getInitFormErrors());
        setPosition(null);
      }}
      handleConfirm={initData ? handleEditCommercialProperty : handleCreateCommercialProperty}
      modalContent={
        <Box component="form">
          {/* Property Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Property name <span style={{color: 'red'}}>*</span>
          </Typography>
          <TextField
            fullWidth
            id="propertyName"
            placeholder="Enter property name"
            variant="outlined"
            size="small"
            value={formData.propertyName}
            onChange={handleChange('propertyName')}
            error={formErrors.propertyName}
            helperText={formErrors.propertyName ? 'Property name is required' : ''}
            sx={{mb: 3}}
          />

          {/* Address */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Address
          </Typography>
          <PlacesAutocomplete
            onSelect={handlePlaceSelect}
            initialValue={formData.description}
            placesAPIs={places}
            inputComponent={params => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="Enter property address"
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'nope',
                }}
                name="location"
                value={formData.description}
              />
            )}
          />

          {/* Time Zone -- auto-filled from the selected address; user can override */}
          <Typography variant="h6" sx={{mb: 1, mt: 3, fontWeight: 400, fontSize: '1.1rem'}}>
            Time Zone
          </Typography>
          <FormControl fullWidth sx={{mb: 3}}>
            <InputLabel id="commercial-timezone-label">Time Zone</InputLabel>
            <Select
              labelId="commercial-timezone-label"
              id="commercial-timezone"
              value={formData.timeZone}
              label="Time Zone"
              onChange={e =>
                setFormData(prev => ({...prev, timeZone: e.target.value as string}))
              }
            >
              {TIMEZONE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
    />
  );
}
